import { z } from 'zod';
import { IsoDateTime, Uuid, patchOf } from './common';
import { Material } from './materials';

const Centavos = z.number().int().min(0).max(100_000_000);
const Text = (max = 200) => z.string().trim().max(max).nullable();

/** Swatches from the Filamentos board ("Cor"). */
/**
 * Quick picks for the filament color (the board's 8 first, then common filament colors). Any other
 * color is chosen with "Outra cor"; `colorHex` accepts any #RRGGBB.
 */
export const FILAMENT_COLORS: { name: string; hex: string }[] = [
  { name: 'Coral', hex: '#FF5C5C' },
  { name: 'Branco', hex: '#FFFFFF' },
  { name: 'Preto', hex: '#1D1D1D' },
  { name: 'Verde', hex: '#2E7D4F' },
  { name: 'Azul', hex: '#2F6FB5' },
  { name: 'Amarelo', hex: '#E8C547' },
  { name: 'Cinza', hex: '#8E8E8E' },
  { name: 'Dourado', hex: '#C9A227' },
  { name: 'Prata', hex: '#C0C4C8' },
  { name: 'Bronze', hex: '#A97142' },
  { name: 'Cobre', hex: '#B4643C' },
  { name: 'Vermelho', hex: '#C62828' },
  { name: 'Laranja', hex: '#F28C28' },
  { name: 'Rosa', hex: '#F2A5C0' },
  { name: 'Roxo', hex: '#6A3FA0' },
  { name: 'Azul-claro', hex: '#7CC3E8' },
  { name: 'Verde-claro', hex: '#8BC34A' },
  { name: 'Marrom', hex: '#6D4C41' },
  { name: 'Bege', hex: '#E6D5B8' },
  { name: 'Transparente', hex: '#E8EEF0' },
];

const FilamentFields = z.object({
  brand: z.string().trim().min(1).max(80),
  material: Material,
  name: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .describe('Product line and color, e.g. "PLA Basic Coral".'),
  colorName: Text(60).default(null),
  colorHex: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .default(null),
  diameterMm: z.number().min(1).max(3).default(1.75),
  density: z
    .number()
    .min(0.5)
    .max(3)
    .optional()
    .describe('g/cm³; defaults to the material default.'),
  netWeightGrams: z
    .number()
    .int()
    .min(1)
    .max(100_000)
    .default(1000)
    .describe('Nominal net weight per spool.'),
  emptySpoolGrams: z
    .number()
    .int()
    .min(0)
    .max(10_000)
    .nullable()
    .default(null)
    .describe('Empty spool weight, for weigh-ins.'),
  pricePerKgCentavos: Centavos.describe(
    'Replacement cost per kg, shipping included. Quotes use it.',
  ),
  nozzleTempC: z.number().int().min(0).max(500).nullable().optional(),
  bedTempC: z.number().int().min(0).max(200).nullable().optional(),
  printNotes: Text(2000).default(null).describe('Temperatures, drying needs, quirks.'),
  supplierLinks: z.array(z.url().max(500)).max(10).default([]),
  minimumGrams: z
    .number()
    .int()
    .min(0)
    .max(1_000_000)
    .nullable()
    .default(null)
    .describe('Low-stock threshold (used with the inventory).'),
  externalRef: Text().default(null),
});

export const CreateFilamentBody = FilamentFields.meta({
  example: {
    brand: 'Bambu Lab',
    material: 'PLA',
    name: 'PLA Basic Coral',
    colorName: 'Coral',
    colorHex: '#FF5C5C',
    pricePerKgCentavos: 11_990,
  },
});
export type CreateFilamentBody = z.input<typeof CreateFilamentBody>;
export const UpdateFilamentBody = patchOf(FilamentFields);
export type UpdateFilamentBody = z.input<typeof UpdateFilamentBody>;

export const Filament = z
  .object({
    id: Uuid,
    brand: z.string(),
    material: Material,
    name: z.string(),
    colorName: z.string().nullable(),
    colorHex: z.string().nullable(),
    diameterMm: z.number(),
    density: z.number(),
    netWeightGrams: z.number().int(),
    emptySpoolGrams: z.number().int().nullable(),
    pricePerKgCentavos: z.number().int(),
    nozzleTempC: z.number().int().nullable(),
    bedTempC: z.number().int().nullable(),
    printNotes: z.string().nullable(),
    supplierLinks: z.array(z.string()),
    minimumGrams: z.number().int().nullable(),
    stockGrams: z.number().describe('Grams left on spools that are not empty.'),
    spoolCount: z.number().int().describe('Spools that are not empty.'),
    low: z.boolean().describe('Has a minimum and the stock is below it.'),
    externalRef: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Filament' });
export type Filament = z.infer<typeof Filament>;

export const FilamentList = z.object({
  data: z.array(Filament),
  nextCursor: z.string().nullable(),
});
export const FilamentQuery = z.object({
  material: Material.optional(),
  low: z.enum(['true', 'false']).optional().describe('Only filaments below their minimum.'),
  q: z.string().trim().max(100).optional().describe('Search in name, brand and color.'),
});
